import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {AlertFillIcon} from '@primer/octicons-react'
import {useMemo} from 'react'

import {LABELS} from './constants/labels'
import type {MilestoneNode} from './repository-milestone-index-page-query-types'
import styles from './RepositoryMilestone.module.css'

// RELAY-CLEANUP(RepositoryMilestonesInternal): widens nullable fields so the still-Relay parent
// data is assignable. Remove when RepositoryMilestonesInternal leaves Relay.
export type MilestoneDateFragment = RelayCompat<Pick<MilestoneNode, 'closed' | 'dueOn'>>

type MilestoneDateProps = {
  milestone: MilestoneDateFragment
}

export function MilestoneDate({milestone}: MilestoneDateProps) {
  const totalOverDue = useMemo(() => {
    if (!milestone || !milestone.dueOn) return null
    const dueOn = new Date(milestone.dueOn)
    const today = new Date()

    // Set both dates to midnight UTC to avoid timezone issues
    // (dueOn is an ISO-8601 UTC string, so we must compare in UTC)
    today.setUTCHours(0, 0, 0, 0)
    dueOn.setUTCHours(0, 0, 0, 0)

    // If a milestone is due today, we don't consider it as overdue
    if (dueOn >= today) return null

    const diffTime = today.getTime() - dueOn.getTime()

    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays < 30) {
      return `${diffDays} day(s)`
    }

    const diffMonths = Math.floor(diffDays / 30)
    if (diffMonths < 12) {
      return `${diffMonths} month(s)`
    }

    const diffYears = Math.floor(diffMonths / 12)
    return `${diffYears} year(s)`
  }, [milestone])

  const formattedDate = useMemo(() => {
    if (!milestone.dueOn) return null

    const dueDate = new Date(milestone.dueOn)

    return dueDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    })
  }, [milestone.dueOn])

  return (
    <>
      {totalOverDue && !milestone.closed ? (
        <div className={styles.milestoneData}>
          <div className={styles.overDue}>
            <AlertFillIcon size={12} />
            <span>
              {LABELS.milestoneOverdue} {totalOverDue}
            </span>
          </div>
          <span className={styles.middot}>{LABELS.separator}</span>
        </div>
      ) : null}
      <div className={styles.milestoneData}>
        {milestone.dueOn ? (
          <span>
            {LABELS.dueBy} {formattedDate}
          </span>
        ) : (
          <span>{LABELS.noDueDate}</span>
        )}
      </div>
    </>
  )
}
